import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useAuthApi } from "../hooks/useAuthApi";
import { MGMT } from "../env";
import type { Company, CompanyDomainVerification, CompanyMember } from "../types";

interface InvitePayload {
  email: string;
  name: string;
  phone: string;
  create_app_auth_method: boolean;
}

const EMPTY_INVITE: InvitePayload = {
  email: "",
  name: "",
  phone: "",
  create_app_auth_method: true,
};

export default function Companies() {
  const authApi = useAuthApi();
  const [company, setCompany] = useState<Company | null>(null);
  const [members, setMembers] = useState<CompanyMember[]>([]);
  const [loadingCompany, setLoadingCompany] = useState(true);
  const [loadingMembers, setLoadingMembers] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invite, setInvite] = useState<InvitePayload>(EMPTY_INVITE);
  const [submittingInvite, setSubmittingInvite] = useState(false);
  const [linkedDomains, setLinkedDomains] = useState<CompanyDomainVerification[]>([]);
  const [loadingDomains, setLoadingDomains] = useState(false);
  const [domainToLink, setDomainToLink] = useState("");
  const [submittingDomainLink, setSubmittingDomainLink] = useState(false);
  const [selectedVerifiedDomain, setSelectedVerifiedDomain] = useState("");
  const [submittingDomainInvite, setSubmittingDomainInvite] = useState(false);
  const [domainActionId, setDomainActionId] = useState<number | null>(null);
  const [rotatingCode, setRotatingCode] = useState(false);
  const [membershipActionId, setMembershipActionId] = useState<number | null>(null);

  const loadCompany = async () => {
    setLoadingCompany(true);
    setError(null);
    try {
      const companies = (await authApi<Company[]>(`${MGMT}/companies`)) ?? [];
      if (companies.length === 0) {
        setCompany(null);
        setMembers([]);
      } else if (companies.length > 1) {
        setCompany(null);
        setMembers([]);
        setError("Your account is linked to multiple companies. Fleet portal supports one company per user.");
      } else {
        setCompany(companies[0]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load company");
    } finally {
      setLoadingCompany(false);
    }
  };

  const loadMembers = async (companyId: number) => {
    setLoadingMembers(true);
    setError(null);
    try {
      const data = await authApi<CompanyMember[]>(`${MGMT}/companies/${companyId}/memberships`);
      setMembers(data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load company members");
      setMembers([]);
    } finally {
      setLoadingMembers(false);
    }
  };

  const loadDomainVerifications = async (companyId: number) => {
    setLoadingDomains(true);
    setError(null);
    try {
      const data = await authApi<CompanyDomainVerification[]>(`${MGMT}/companies/${companyId}/domains`);
      setLinkedDomains(data ?? []);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to load linked domains");
      setLinkedDomains([]);
    } finally {
      setLoadingDomains(false);
    }
  };

  useEffect(() => {
    void loadCompany();
  }, []);

  useEffect(() => {
    if (company == null) {
      setMembers([]);
      setLinkedDomains([]);
      return;
    }
    void loadMembers(company.id);
    void loadDomainVerifications(company.id);
  }, [company?.id]);

  const rotateJoinCode = async () => {
    if (company == null) return;
    setRotatingCode(true);
    setError(null);
    try {
      const updated = await authApi<Company>(`${MGMT}/companies/${company.id}/join-code/rotate`, {
        method: "POST",
      });
      if (updated) {
        setCompany(updated);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to rotate join code");
    } finally {
      setRotatingCode(false);
    }
  };

  const submitInvite = async () => {
    if (company == null || !invite.email.trim() || !invite.name.trim()) return;
    setSubmittingInvite(true);
    setError(null);
    try {
      await authApi(`${MGMT}/companies/${company.id}/memberships`, {
        method: "POST",
        body: JSON.stringify({
          email: invite.email.trim(),
          name: invite.name.trim(),
          phone: invite.phone.trim() || undefined,
          create_app_auth_method: invite.create_app_auth_method,
        }),
      });
      setInvite(EMPTY_INVITE);
      await loadMembers(company.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to invite driver");
    } finally {
      setSubmittingInvite(false);
    }
  };

  const submitDomainInvite = async () => {
    if (company == null || !selectedVerifiedDomain.trim()) return;
    setSubmittingDomainInvite(true);
    setError(null);
    try {
      await authApi(`${MGMT}/companies/${company.id}/memberships/domain`, {
        method: "POST",
        body: JSON.stringify({ domain: selectedVerifiedDomain.trim() }),
      });
      setSelectedVerifiedDomain("");
      await loadMembers(company.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to add drivers by domain");
    } finally {
      setSubmittingDomainInvite(false);
    }
  };

  const submitDomainLink = async () => {
    if (company == null || !domainToLink.trim()) return;
    setSubmittingDomainLink(true);
    setError(null);
    try {
      await authApi(`${MGMT}/companies/${company.id}/domains`, {
        method: "POST",
        body: JSON.stringify({ domain: domainToLink.trim() }),
      });
      setDomainToLink("");
      await loadDomainVerifications(company.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to link domain");
    } finally {
      setSubmittingDomainLink(false);
    }
  };

  const verifyDomain = async (domainId: number) => {
    if (company == null) return;
    setDomainActionId(domainId);
    setError(null);
    try {
      await authApi(`${MGMT}/companies/${company.id}/domains/${domainId}/verify`, {
        method: "POST",
      });
      await loadDomainVerifications(company.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to verify domain");
    } finally {
      setDomainActionId(null);
    }
  };

  const statusColor = (status: CompanyDomainVerification["status"]): "default" | "success" | "warning" => {
    if (status === "verified") return "success";
    if (status === "verification_expired") return "warning";
    return "default";
  };

  const statusLabel = (status: CompanyDomainVerification["status"]): string => {
    if (status === "verified") return "Verified";
    if (status === "verification_expired") return "Verification expired";
    return "Awaiting verification";
  };

  const verifiedDomains = linkedDomains.filter((item) => item.status === "verified");

  const removeMember = async (driverId: number) => {
    if (company == null) return;
    setMembershipActionId(driverId);
    setError(null);
    try {
      await authApi(`${MGMT}/companies/${company.id}/memberships/${driverId}`, { method: "DELETE" });
      await loadMembers(company.id);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to remove company member");
    } finally {
      setMembershipActionId(null);
    }
  };

  if (loadingCompany) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error">{error}</Alert>}
      {company == null ? (
        <Alert severity="info">
          No company is assigned to your account. Provision fleet company access in the admin portal first.
        </Alert>
      ) : (
        <>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ md: "center" }}>
              <Chip label={`Company: ${company.name}`} color="default" variant="outlined" />
              <Chip label={`Join code: ${company.join_code}`} color="primary" variant="outlined" />
              <Button variant="contained" onClick={rotateJoinCode} disabled={rotatingCode}>
                {rotatingCode ? "Rotating…" : "Rotate join code"}
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Invite driver
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Driver name"
                size="small"
                value={invite.name}
                onChange={(event) => setInvite((current) => ({ ...current, name: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Driver email"
                size="small"
                value={invite.email}
                onChange={(event) => setInvite((current) => ({ ...current, email: event.target.value }))}
                fullWidth
              />
              <TextField
                label="Phone (optional)"
                size="small"
                value={invite.phone}
                onChange={(event) => setInvite((current) => ({ ...current, phone: event.target.value }))}
                fullWidth
              />
              <Button
                variant="contained"
                onClick={submitInvite}
                disabled={submittingInvite || !invite.email.trim() || !invite.name.trim()}
              >
                {submittingInvite ? "Inviting…" : "Invite"}
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Link company domain
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <TextField
                label="Domain"
                size="small"
                value={domainToLink}
                onChange={(event) => setDomainToLink(event.target.value)}
                helperText="Create TXT record from table below, then verify."
                fullWidth
              />
              <Button
                variant="contained"
                onClick={submitDomainLink}
                disabled={submittingDomainLink || !domainToLink.trim()}
              >
                {submittingDomainLink ? "Linking…" : "Link domain"}
              </Button>
            </Stack>
            <Divider sx={{ my: 2 }} />
            {loadingDomains ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
                <CircularProgress size={22} />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Domain</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>TXT name</TableCell>
                    <TableCell>TXT value</TableCell>
                    <TableCell>Verified</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {linkedDomains.map((domain) => (
                    <TableRow key={domain.id}>
                      <TableCell>{domain.domain}</TableCell>
                      <TableCell>
                        <Chip size="small" color={statusColor(domain.status)} label={statusLabel(domain.status)} />
                      </TableCell>
                      <TableCell>{domain.verification_record_name}</TableCell>
                      <TableCell>{domain.verification_record_value}</TableCell>
                      <TableCell>
                        {domain.verified_at ? new Date(domain.verified_at).toLocaleDateString() : "-"}
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => verifyDomain(domain.id)}
                          disabled={domainActionId === domain.id}
                        >
                          {domainActionId === domain.id ? "Verifying…" : "Verify now"}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {linkedDomains.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No linked domains yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Add drivers by verified domain
            </Typography>
            <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
              <FormControl size="small" fullWidth>
                <InputLabel id="verified-domain-label">Verified domain</InputLabel>
                <Select<string>
                  labelId="verified-domain-label"
                  value={selectedVerifiedDomain}
                  label="Verified domain"
                  onChange={(event) => setSelectedVerifiedDomain(event.target.value as string)}
                >
                  {verifiedDomains.map((domain) => (
                    <MenuItem key={domain.id} value={domain.domain}>
                      {domain.domain}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={submitDomainInvite}
                disabled={submittingDomainInvite || !selectedVerifiedDomain.trim()}
              >
                {submittingDomainInvite ? "Adding…" : "Add all matching drivers"}
              </Button>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="h6">Company members</Typography>
            <Divider sx={{ my: 2 }} />
            {loadingMembers ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Join Mode</TableCell>
                    <TableCell>Joined</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell>{member.driver?.name ?? `#${member.driver_id}`}</TableCell>
                      <TableCell>{member.driver?.email ?? "-"}</TableCell>
                      <TableCell>{member.status}</TableCell>
                      <TableCell>{member.join_mode}</TableCell>
                      <TableCell>{new Date(member.created_at).toLocaleDateString()}</TableCell>
                      <TableCell align="right">
                        <Button
                          color="error"
                          size="small"
                          onClick={() => removeMember(member.driver_id)}
                          disabled={membershipActionId === member.driver_id}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                  {members.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={6}>
                        <Typography variant="body2" color="text.secondary">
                          No company members yet.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            )}
          </Paper>
        </>
      )}
    </Stack>
  );
}
